import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IServicePackage } from '@clickersphere/core/entities/IServicePackage';
import { Button } from '../../Button';
import ImageUploadField from '../../Catalog/ImageUploadField';
import { Input } from '../../Input';
import MultiSelect from '../../MultiSelect';
import { useRegisterServicePackageFormController } from './useRegisterServicePackageFormController';

interface IRegisterServicePackageFormProps {
  servicePackage?: IServicePackage;
}

export default function RegisterServicePackageForm({
  servicePackage,
}: IRegisterServicePackageFormProps) {
  const { t } = useTranslation();
  const {
    isFetching,
    errors,
    register,
    control,
    handleSubmit,
    handleFormReset,
    serviceOptions,
    handlePriceChange,
  } = useRegisterServicePackageFormController({ servicePackage });

  return (
    <form
      className="my-4 flex w-full flex-col items-center gap-4"
      onSubmit={handleSubmit}
      noValidate
    >
      <Input
        type="text"
        placeholder={t('catalog.form.fields.name.placeholderPackage')}
        {...register('name')}
        error={errors.name?.message}
        disabled={isFetching}
      />

      <Input
        type="text"
        placeholder={t('catalog.form.fields.description.placeholder')}
        {...register('description')}
        error={errors.description?.message}
        disabled={isFetching}
      />

      <Controller
        name="serviceIds"
        control={control}
        render={({ field }) => (
          <MultiSelect
            options={serviceOptions}
            value={field.value}
            onChange={field.onChange}
            placeholder={t('catalog.form.fields.services.placeholder')}
            searchPlaceholder={t(
              'catalog.form.fields.services.searchPlaceholder',
            )}
            noResultsText={t('catalog.form.fields.services.noResults')}
            error={errors.serviceIds?.message}
            disabled={isFetching}
          />
        )}
      />

      <Input
        type="number"
        inputMode="decimal"
        placeholder={t('catalog.form.fields.price.placeholder')}
        step="0.01"
        min="0"
        {...register('price', {
          valueAsNumber: true,
          onChange: handlePriceChange,
        })}
        error={errors.price?.message}
        disabled={isFetching}
      />

      <ImageUploadField
        name="imageUrl"
        control={control}
        label={t('catalog.form.fields.imagePackage')}
        disabled={isFetching}
        initialImageUrl={servicePackage?.imageUrl}
      />

      <section className="mt-4 flex w-full gap-4">
        <Button
          className="w-50"
          color="ghost"
          disabled={isFetching}
          onClick={() => handleFormReset()}
        >
          {t('catalog.form.actions.clear')}
        </Button>
        <Button type="submit" disabled={isFetching} isLoading={isFetching}>
          {t('catalog.form.actions.save')}
        </Button>
      </section>
    </form>
  );
}
